import { HelpButton } from '../features/help/HelpPanel';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Player, type PlayerRef } from '@remotion/player';
import { AnimatedHtml } from '@nodes/remotion/composition';
import { ELEMENT_MOTION_PRESETS, motionStages, motionLayerState, normalizeTemplateMotion, slideAnimations, type MotionLayer } from '@nodes/remotion-core/motion';

const labels: Record<string, string> = { none: 'None', fade: 'Fade', 'slide-left': 'Slide left', 'zoom-in': 'Zoom', 'soft-float': 'Soft float', 'liquid-letters': 'Liquid letters · Капли в буквы', 'edge-letters':'Letters from edges · Буквы с краёв', 'paper-unfold':'Paper unfold · Расправить газету' };

export function MotionEditor({ editor, setEditor }: { editor: any; setEditor: (value: any) => void }) {
  const [slideIndex, setSlideIndex] = useState(0);
  const [selected, setSelected] = useState('');
  const [capabilities, setCapabilities] = useState<Record<string, { inline: boolean; text?: boolean }>>({});
  const player = useRef<PlayerRef>(null);
  const onCapabilities = useCallback((values: Record<string, { inline: boolean; text?: boolean }>) => setCapabilities(values), []);
  const template = useMemo(() => normalizeTemplateMotion({ ...editor, slides: editor.analysis.slides }), [editor]);
  const slide = template.slides[slideIndex] || template.slides[0];
  const states = motionLayerState(template.layers, editor.bindings).filter(layer => layer.slideIndex === slide.index);
  const byId = new Map<string, MotionLayer>(states.map(layer => [layer.id, layer]));
  const depth = (layer: MotionLayer) => {
    let result = 0, parent = byId.get(layer.parentId || '');
    while (parent) { result++; parent = byId.get(parent.parentId || ''); }
    return result;
  };
  const inputProps = useMemo(() => ({
    html: slide.html,
    attributeAnimations: slideAnimations(template, slide.index),
    selectedElementId: selected,
    onElementCapabilities: onCapabilities,
  }), [template, slide, selected, onCapabilities]);

  return <section className="remotion-motion" aria-label="Element animations">
    <h3>Element animations <HelpButton article="animation" /></h3>
    <label>Card <select aria-label="Animation card" value={slide.index} onChange={event => {
      setSlideIndex(Number(event.target.value)); setSelected(''); setCapabilities({}); player.current?.seekTo(0);
    }}>{template.slides.map((item: any) => <option key={item.index} value={item.index}>Card {item.index + 1}</option>)}</select></label>
    <div className="remotion-motion-layout">
      <div>
        <Player ref={player} component={AnimatedHtml} inputProps={inputProps} durationInFrames={90} fps={30}
          compositionWidth={1080} compositionHeight={1350} controls loop style={{ width: '100%', maxWidth: 360 }} />
        <button className="btn" type="button" onClick={() => { player.current?.seekTo(0); player.current?.play(); }}>Replay animation</button>
        <p className="muted">In plays at the start, Main between entrance and exit, and Out at the end. Each stage is optional. Container effects also move their children. None disables only the element’s own effect.</p>
      </div>
      <div className="remotion-layer-tree" role="tree" aria-label="HTML elements">
        {states.map(layer => {
          const inline = capabilities[layer.id]?.inline ?? layer.inline;
          const text = capabilities[layer.id]?.text ?? /^(h[1-6]|p|span|strong|b|em|i|small|a|li|blockquote)$/.test(layer.tag);
          const stages = motionStages(template.animations[layer.id]);
          const allowed = inline ? ['none', 'fade'] : ELEMENT_MOTION_PRESETS;
          return <div key={layer.id} role="treeitem" aria-level={depth(layer) + 1} aria-selected={selected === layer.id}
            className="remotion-layer" style={{ paddingLeft: 10 + depth(layer) * 14 }}>
            <button type="button" className="btn" onClick={() => { setSelected(layer.id); player.current?.pause(); player.current?.seekTo(24); }}>{layer.label}</button>
            {(['in', 'main', 'out'] as const).map(stage => {
              const options = stage === 'main' ? inline ? ['none'] : ['none', 'soft-float']
                : [...allowed.filter(effect => !['liquid-letters','edge-letters','paper-unfold'].includes(effect)), ...(stage === 'in' && text ? ['liquid-letters','edge-letters'] : []), ...(stage === 'in' && layer.tag === 'img' ? ['paper-unfold'] : [])];
              return <label key={stage}>{stage === 'in' ? 'In' : stage === 'main' ? 'Main' : 'Out'}
                <select aria-label={`${stage === 'in' ? 'In effect' : stage === 'main' ? 'Main effect' : 'Out effect'} ${layer.label}`}
                  disabled={!!layer.blockedBy} value={options.includes(stages[stage]) ? stages[stage] : stage === 'main' ? 'none' : 'fade'}
                  onChange={event => setEditor({ ...editor, animations: { ...template.animations, [layer.id]: { ...stages, [stage]: event.target.value } } })}>
                  {options.map(effect => <option key={effect} value={effect}>{effect === 'fade' ? stage === 'out' ? 'Fade out' : 'Fade in' : effect === 'soft-float' && stage === 'main' ? 'Float' : labels[effect]}</option>)}
                </select>
              </label>;
            })}
            {layer.blockedBy ? <small>Text replacement removes this element. Animate the containing text field.</small>
              : stages.in === 'liquid-letters' ? <small>Капли поочерёдно всплывают и превращаются в буквы. После появления текст возвращается к исходной вёрстке.</small>
              : inline ? <small>Inline text: Fade or Liquid letters.</small> : null}
          </div>;
        })}
      </div>
    </div>
  </section>;
}
